import {
  invoiceKindToUploadDocumentType,
  UploadDocumentType,
} from '~/components/uploadDocument/AddManuallyDropdown';
import type { BaseLinkedAttachment } from '~/data/schemas/attachment';
import { AttachmentType, byPageNumber, isOriginalDocumentPage } from '~/data/schemas/attachment';
import type { Invoice } from '~/data/schemas/invoice';
import type { Ticket } from '~/data/schemas/ticket';

/**
 * A document the detail page's viewer can render: one source PDF plus the specific
 * pages of it this record owns.
 *
 * An invoice's `attachments[]` are page-level links into a source PDF, and one source
 * PDF can hold several invoices when it came from a multi-document upload — so the
 * viewer renders an explicit page list rather than "all pages of the file".
 */
export interface InvoiceViewerDocument {
  /** Stable key: the underlying attachment id, which all its pages share. */
  id: string;
  filePath: string;
  /** 1-based page numbers within `filePath`, ascending. */
  pageNumbers: number[];
  label: string;
}

const isRenderablePdf = (
  attachment: BaseLinkedAttachment,
): attachment is BaseLinkedAttachment & { attachmentUrl: string; pageNumber: number } =>
  attachment.fileType === AttachmentType.Pdf &&
  attachment.attachmentUrl !== null &&
  attachment.pageNumber !== null;

/**
 * Group page-level attachments into one document per source file, preserving page
 * order. Pages of the same file are almost always contiguous, but nothing guarantees
 * it, so group by `attachmentId` rather than assuming a range.
 */
const groupByFile = (
  attachments: BaseLinkedAttachment[],
  label: string,
): InvoiceViewerDocument[] => {
  const documents = new Map<string, InvoiceViewerDocument>();

  for (const attachment of [...attachments].sort(byPageNumber)) {
    if (!isRenderablePdf(attachment)) continue;

    const existing = documents.get(attachment.attachmentId);
    if (existing) {
      existing.pageNumbers.push(attachment.pageNumber);
    } else {
      documents.set(attachment.attachmentId, {
        id: attachment.attachmentId,
        filePath: attachment.attachmentUrl,
        pageNumbers: [attachment.pageNumber],
        label,
      });
    }
  }

  return [...documents.values()];
};

/**
 * The invoice's own document — the pages it was extracted from, using the same
 * `isOriginalDocumentPage` predicate as the Attachments tab and as the API's own
 * stamped-PDF builder. Voided pages (superseded by a re-upload) are supporting data,
 * not the invoice.
 *
 * This filter and `getSupportingDocuments`' must stay exact complements of each other:
 * `InvoiceDocumentViewer` unions the page lists of any two entries sharing an
 * `attachmentId`, and that union only reconstructs a complete source file if every page
 * lands in exactly one bucket.
 */
export function getPrimaryDocuments(invoice: Invoice | undefined): InvoiceViewerDocument[] {
  if (!invoice) return [];

  const kindLabel = invoiceKindToUploadDocumentType[invoice.kind];

  return groupByFile(
    invoice.attachments.filter(isOriginalDocumentPage),
    `${kindLabel} #${invoice.number}`,
  );
}

/** A ticket's own PDFs, for the ticket row preview. */
export function getTicketDocuments(ticket: Ticket | undefined): InvoiceViewerDocument[] {
  if (!ticket) return [];
  return groupByFile(ticket.attachments ?? [], `Ticket #${ticket.number}`);
}

/**
 * Everything else attached to the invoice or its linked tickets, offered in the
 * viewer's document switcher. Separate from the primary document so the viewer's
 * default scroll is only the invoice itself.
 */
export function getSupportingDocuments(
  invoice: Invoice | undefined,
  tickets: Ticket[] | undefined,
): InvoiceViewerDocument[] {
  if (!invoice) return [];

  const supporting = groupByFile(
    invoice.attachments.filter((attachment) => !isOriginalDocumentPage(attachment)),
    UploadDocumentType.supportingData,
  );

  const ticketDocuments = (tickets ?? []).flatMap((ticket) =>
    groupByFile(ticket.attachments ?? [], `Ticket #${ticket.number}`),
  );

  return [...supporting, ...ticketDocuments];
}
